import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Grid2, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Pie, PieChart, ResponsiveContainer, Sector,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import { Donut, IconBubble, InfoTip, Panel, PanelState, TooltipBox, sectionState, useSurface } from "./OverviewUi";
import {
  CATEGORY_META, FILTER_METHODS, METHOD_META, PALETTE, compactNumber, formatLkr, longDate, longMonth, monthLabel,
  overviewApi, plural, rangeText, shortDate, sriLankaToday, weekdayDate,
} from "./overviewUtils";

// Lighter, still solid shade of a palette colour (for "expected" and upcoming bars).
const EXPECTED_COLOR = "#b9bdf9";
const UPCOMING_COLOR = "#d9dbfc";

const axisProps = (s) => ({ tick: { fontSize: 12, fill: s.axis }, tickLine: false, axisLine: { stroke: s.grid } });
const axisLabel = (s, value, angle = 0) => ({
  value, angle, position: angle ? "insideLeft" : "insideBottom", offset: angle ? 12 : -2,
  style: { fontSize: 12, fill: s.axis, textAnchor: "middle" },
});

// ---------------------------------------------------------------- attendance trend
export function AttendanceTrend({ section, failed, range, onDay }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const days = section?.days || [];
  return (
    <Panel
      icon={<IconBubble Icon={EventAvailableOutlinedIcon} color={PALETTE.blue} size={36} />}
      title="Attendance"
      subtitle="Members per day"
    >
      <PanelState state={state} reason={section?.reason} empty={!days.some((d) => d.members)} height={280}
        emptyText={`No check-ins between ${rangeText(range.from, range.to)}.`}>
        <Typography sx={{ fontSize: 14, color: s.muted, mb: 1 }}>
          <Box component="span" sx={{ fontWeight: 800, color: s.text }}>{(section?.totals?.members || 0).toLocaleString()}</Box> check-ins
        </Typography>
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days} margin={{ top: 8, right: 8, left: 8, bottom: 12 }}
              onClick={(chart) => chart?.activeLabel && onDay(chart.activeLabel)} style={{ cursor: "pointer" }}>
              <CartesianGrid vertical={false} stroke={s.grid} />
              <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={16} {...axisProps(s)} label={axisLabel(s, "Day")} />
              <YAxis allowDecimals={false} width={48} {...axisProps(s)} label={axisLabel(s, "Members", -90)} />
              <ChartTooltip cursor={{ fill: s.hover }} content={({ active, payload, label }) => (active && payload?.length ? (
                <TooltipBox title={weekdayDate(label)} rows={[{ label: "Members checked in", value: payload[0].payload.members, color: PALETTE.blue }]} />
              ) : null)} />
              <Bar dataKey="members" name="Members" fill={PALETTE.blue} maxBarSize={36} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- filters
const selectSx = { minWidth: 150, "& .MuiInputBase-input": { fontSize: 13 }, "& .MuiInputLabel-root": { fontSize: 13 } };

export function MethodCategoryFilters({ method, category, onMethod, onCategory }) {
  return (
    <>
      <TextField select size="small" label="Method" value={method} onChange={(e) => onMethod(e.target.value)} sx={selectSx}>
        <MenuItem value="all">All methods</MenuItem>
        {FILTER_METHODS.map((m) => <MenuItem key={m} value={m}>{METHOD_META[m].label}</MenuItem>)}
      </TextField>
      <TextField select size="small" label="Package" value={category} onChange={(e) => onCategory(e.target.value)} sx={selectSx}>
        <MenuItem value="all">All packages</MenuItem>
        {Object.entries(CATEGORY_META).map(([key, meta]) => <MenuItem key={key} value={key}>{meta.label}</MenuItem>)}
      </TextField>
    </>
  );
}

// ---------------------------------------------------------------- income: expected vs actual
function SummaryFigure({ label, value, color, info }) {
  const s = useSurface();
  return (
    <Box sx={{ flex: "1 1 130px", minWidth: 0, p: 1.25, border: `1px solid ${s.border}`, borderRadius: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: s.muted }}>{label}</Typography>
        <InfoTip text={info} />
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: color || s.text, wordBreak: "break-word" }}>{value}</Typography>
    </Box>
  );
}

const pctText = (pct) => (pct === null || pct === undefined ? "—" : `${pct}%`);
const collectedText = (pct) => (pct === null || pct === undefined ? "—" : `${pct}% collected`);

export function IncomeChart({ section, failed, range, refreshKey, onMonth }) {
  const s = useSurface();
  const [method, setMethod] = useState("all");
  const [category, setCategory] = useState("all");
  const [filtered, setFiltered] = useState({ data: null, failed: false });
  const latest = useRef(0);
  const hasFilter = method !== "all" || category !== "all";

  useEffect(() => {
    if (!hasFilter) return;
    const id = ++latest.current;
    setFiltered((f) => ({ ...f, failed: false }));
    overviewApi("/income", { from: range.from, to: range.to, method, category })
      .then((data) => { if (id === latest.current) setFiltered({ data, failed: false }); })
      .catch(() => { if (id === latest.current) setFiltered({ data: null, failed: true }); });
  }, [hasFilter, method, category, range.from, range.to, refreshKey]);

  const shown = hasFilter ? filtered.data : section;
  const state = sectionState(shown, hasFilter ? filtered.failed : failed);
  const byMethod = method !== "all";
  const today = sriLankaToday();
  const bars = useMemo(() => [
    ...(shown?.months || []).map((m) => ({
      ...m, slot: m.from, upcoming: false, expectedValue: Number(m.expected) || 0, actualValue: Number(m.actual) || 0,
    })),
    ...(shown?.upcoming || []).map((m) => ({
      ...m, slot: m.from, upcoming: true, expectedValue: Number(m.expected) || 0, actualValue: null,
    })),
  ], [shown]);
  const summary = shown?.summary;
  const difference = Number(summary?.difference) || 0;
  const label = (bar) => {
    if (bar.upcoming) return `${longMonth(bar.key)} · ${bar.partial ? "rest of month" : "upcoming"}`;
    return `${longMonth(bar.key)}${bar.to === today && bar.from.slice(0, 7) === today.slice(0, 7) ? " · month to date" : ""}`;
  };
  const empty = !bars.some((b) => b.expectedValue || b.actualValue);

  return (
    <Panel
      icon={<IconBubble Icon={PaymentsOutlinedIcon} color={PALETTE.purple} size={36} />}
      title="Income"
      subtitle="Expected vs received"
      info="Expected = renewals due + new registrations."
      action={<MethodCategoryFilters method={method} category={category} onMethod={setMethod} onCategory={setCategory} />}
    >
      <PanelState state={state} reason={shown?.reason} empty={empty} height={320}
        emptyText={`Nothing expected or received between ${rangeText(range.from, range.to)}${hasFilter ? " for these filters" : ""}.`}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <SummaryFigure label="Expected" value={formatLkr(summary?.expected)} />
          <SummaryFigure label="Received" value={formatLkr(summary?.actual)} />
          <SummaryFigure label="Difference" value={`${difference > 0 ? "+" : ""}${formatLkr(summary?.difference)}`}
            color={difference < 0 ? PALETTE.red : difference > 0 ? PALETTE.green : undefined} />
          <SummaryFigure label="Collected" value={byMethod ? "—" : pctText(summary?.collection_pct)}
            info={byMethod ? "Not shown with a payment method filter." : undefined} />
        </Box>
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} margin={{ top: 8, right: 8, left: 16, bottom: 12 }} barGap={2}
              onClick={(chart) => {
                const picked = bars.find((b) => b.slot === chart?.activeLabel);
                if (picked) onMonth(picked, { method, category });
              }}
              style={{ cursor: "pointer" }}>
              <CartesianGrid vertical={false} stroke={s.grid} />
              <XAxis dataKey="slot" tickFormatter={(slot) => {
                const bar = bars.find((b) => b.slot === slot);
                return bar ? monthLabel(bar.key) : "";
              }} {...axisProps(s)} label={axisLabel(s, "Month")} />
              <YAxis tickFormatter={compactNumber} width={56} {...axisProps(s)} label={axisLabel(s, "LKR", -90)} />
              <ChartTooltip cursor={{ fill: s.hover }} content={({ active, payload }) => (active && payload?.length ? (
                <TooltipBox title={label(payload[0].payload)} rows={[
                  { label: "Expected", value: formatLkr(payload[0].payload.expected), color: EXPECTED_COLOR },
                  ...(payload[0].payload.upcoming ? [] : [
                    { label: "Received", value: formatLkr(payload[0].payload.actual), color: PALETTE.purple },
                    ...(byMethod ? [] : [{ label: collectedText(payload[0].payload.collection_pct), value: "" }]),
                  ]),
                ]} />
              ) : null)} />
              <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} payload={[
                { value: "Expected", type: "square", color: EXPECTED_COLOR },
                { value: "Received", type: "square", color: PALETTE.purple },
                { value: "Upcoming", type: "square", color: UPCOMING_COLOR },
              ]} />
              <Bar dataKey="expectedValue" name="Expected" maxBarSize={34} isAnimationActive={false} cursor="pointer">
                {bars.map((b) => <Cell key={`e-${b.slot}`} fill={b.upcoming ? UPCOMING_COLOR : EXPECTED_COLOR} />)}
              </Bar>
              <Bar dataKey="actualValue" name="Received" fill={PALETTE.purple} maxBarSize={34} isAnimationActive={false}
                cursor="pointer" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- income donuts
/** Exact amount: whole rupees as "LKR 15,000", cents only when there are any. */
const exactLkr = (value) => {
  const n = Number(value) || 0;
  return formatLkr(n, Number.isInteger(n) ? 0 : 2);
};

const shareText = (pct) => `${Number((Number(pct) || 0).toFixed(1))}%`;

/** "1 Oct – 30 Oct 2026" (the start year is shown only when it differs from the end year). */
const breakdownRange = (from, to) => {
  if (!from || !to) return "";
  if (from === to) return longDate(to);
  return `${from.slice(0, 4) === to.slice(0, 4) ? shortDate(from) : longDate(from)} – ${longDate(to)}`;
};

const DONUT_SIZE = 180;
// Slices below this share are drawn at this share so they stay visible; numbers are unchanged.
const MIN_SLICE_SHARE = 0.02;

/** Font size that keeps the exact total inside the ring. */
const centreFontSize = (text) => {
  const usable = DONUT_SIZE * 0.66 - 22;
  return Math.max(11, Math.min(20, Math.floor(usable / (text.length * 0.6))));
};

function RevenueDonut({ title, section, meta, failed, range }) {
  const s = useSurface();
  const [hovered, setHovered] = useState(null);
  const state = sectionState(section, failed);
  const total = Number(section?.total) || 0;
  const rows = (section?.items || [])
    .map((i) => {
      const amount = Number(i.amount) || 0;
      const known = meta[i.key] || METHOD_META.other;
      const pct = i.pct !== null && i.pct !== undefined ? Number(i.pct) : total ? (amount / total) * 100 : 0;
      return {
        key: i.key, label: known.label, color: known.color, amount, pct,
        payments: i.payments === null || i.payments === undefined ? null : Number(i.payments),
        slice: Math.max(amount, total * MIN_SLICE_SHARE),
      };
    })
    .filter((r) => r.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  const activeIndex = rows.findIndex((r) => r.key === hovered);
  const totalText = exactLkr(total);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: s.text, mb: 1.5 }}>{title}</Typography>
      <PanelState state={state} reason={section?.reason} empty={!total || !rows.length} height={DONUT_SIZE}
        emptyText={`No payments between ${rangeText(range.from, range.to)}`}>
        <Box sx={{ position: "relative", width: DONUT_SIZE, maxWidth: "100%", aspectRatio: "1 / 1", mx: "auto" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={rows} dataKey="slice" nameKey="label" innerRadius="66%" outerRadius="92%"
                paddingAngle={rows.length > 1 ? 2 : 0} stroke="none" isAnimationActive={false}
                activeIndex={activeIndex >= 0 ? activeIndex : undefined}
                activeShape={(props) => <Sector {...props} outerRadius={props.outerRadius + 5} />}
                onMouseEnter={(_, index) => setHovered(rows[index]?.key ?? null)}
                onMouseLeave={() => setHovered(null)}>
                {rows.map((row) => (
                  <Cell key={row.key} fill={row.color} style={{ outline: "none" }}
                    fillOpacity={hovered && hovered !== row.key ? 0.35 : 1} />
                ))}
              </Pie>
              <ChartTooltip content={({ active, payload }) => (active && payload?.length ? (
                <TooltipBox title={payload[0].payload.label} rows={[
                  { label: "Amount", value: exactLkr(payload[0].payload.amount), color: payload[0].payload.color },
                  { label: "Share", value: shareText(payload[0].payload.pct) },
                ]} />
              ) : null)} />
            </PieChart>
          </ResponsiveContainer>
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", pointerEvents: "none", textAlign: "center" }}>
            <Typography sx={{ fontSize: 12, color: s.muted, lineHeight: 1.3 }}>Total</Typography>
            <Typography sx={{ fontSize: centreFontSize(totalText), fontWeight: 800, color: s.text, lineHeight: 1.2,
              whiteSpace: "nowrap" }}>{totalText}</Typography>
          </Box>
        </Box>

        {/* Legend: one grid so amounts and shares line up in columns */}
        <Box
          sx={{ mt: 2, mx: "auto", maxWidth: 460, display: "grid",
            gridTemplateColumns: "10px minmax(0, 1fr) max-content max-content",
            columnGap: 1.25, alignItems: "start" }}>
          {rows.map((row, index) => {
            const on = hovered === row.key;
            const cell = {
              py: 0.9, borderTop: index ? `1px solid ${s.border}` : "none",
              bgcolor: on ? s.hover : "transparent", transition: "background-color 120ms",
            };
            return (
              <Box key={row.key} sx={{ display: "contents" }}
                onMouseEnter={() => setHovered(row.key)} onMouseLeave={() => setHovered(null)}>
                <Box sx={{ ...cell, display: "flex", alignItems: "center", height: "100%", pl: 0.25 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: row.color, mt: "5px" }} />
                </Box>
                <Box sx={{ ...cell, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, color: s.text, fontWeight: on ? 700 : 500, overflowWrap: "anywhere",
                    lineHeight: 1.35 }}>{row.label}</Typography>
                  {row.payments !== null && (
                    <Typography sx={{ fontSize: 12, color: s.muted, lineHeight: 1.35 }}>
                      {plural(row.payments, "payment")}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ ...cell, fontSize: 14, fontWeight: 700, color: s.text, textAlign: "right",
                  whiteSpace: "nowrap", lineHeight: 1.35 }}>{exactLkr(row.amount)}</Typography>
                <Typography sx={{ ...cell, fontSize: 13, color: s.muted, textAlign: "right", whiteSpace: "nowrap",
                  minWidth: 44, lineHeight: 1.45, pr: 0.25 }}>{shareText(row.pct)}</Typography>
              </Box>
            );
          })}
        </Box>
      </PanelState>
    </Box>
  );
}

export function RevenueDonuts({ byMethod, byCategory, failed, range }) {
  return (
    <Panel
      icon={<IconBubble Icon={DonutLargeOutlinedIcon} color={PALETTE.purple} size={36} />}
      title="Income breakdown"
      subtitle={breakdownRange(range.from, range.to)}
      info="Recorded payments by method and by package."
    >
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <RevenueDonut title="By payment method" section={byMethod} meta={METHOD_META} failed={failed} range={range} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <RevenueDonut title="By package" section={byCategory} meta={CATEGORY_META} failed={failed} range={range} />
        </Grid2>
      </Grid2>
    </Panel>
  );
}

// ---------------------------------------------------------------- membership status
const STATUS_ITEMS = {
  active: { label: "Active", color: PALETTE.green, Icon: CheckCircleOutlineIcon },
  frozen: { label: "Frozen", color: PALETTE.teal, Icon: AcUnitIcon },
  expired: { label: "Expired (incl. grace)", color: PALETTE.red, Icon: ErrorOutlineIcon },
  pending: { label: "Pending / under review", color: PALETTE.amber, Icon: HourglassEmptyIcon },
  restricted: { label: "Restricted", color: PALETTE.grey, Icon: BlockIcon },
};

export function MembershipStatus({ section, failed }) {
  const state = sectionState(section, failed);
  const items = (section?.items || []).map((i) => ({
    ...STATUS_ITEMS[i.key], key: i.key, value: i.count, display: i.count.toLocaleString(),
  }));
  return (
    <Panel
      icon={<IconBubble Icon={DonutLargeOutlinedIcon} color={PALETTE.green} size={36} />}
      title="Membership status"
      subtitle="Today"
    >
      <PanelState state={state} reason={section?.reason} empty={!section?.total} height={200} emptyText="No registrations yet.">
        <Donut items={items} centerValue={(section?.active ?? 0).toLocaleString()} centerLabel="active members" />
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- members by package
export const PACKAGE_CATEGORY_META = {
  individual: { label: "Individual", color: PALETTE.blue },
  student: { label: "Student", color: PALETTE.teal },
  couple: { label: "Couple", color: PALETTE.pink },
  family: { label: "Family", color: PALETTE.green },
  founding: { label: "Founding", color: PALETTE.amber },
  other: { label: "Other", color: PALETTE.grey },
};
const PACKAGE_STATUS_META = { ...STATUS_ITEMS, other: { label: "Other", color: "#6c757d" } };
const PACKAGE_STATUSES = ["active", "frozen", "expired", "pending", "restricted", "other"];
const PACKAGE_ROW_HEIGHT = 34;

/** Package names on the Y axis, wrapped onto two lines when long (never cut off). */
const wrapLabel = (text, max = 15) => {
  const lines = [];
  (text || "").split(" ").forEach((word) => {
    const last = lines[lines.length - 1];
    if (last && `${last} ${word}`.length <= max) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  });
  return lines;
};

function LegendDot({ color, label }) {
  const s = useSurface();
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: color, flexShrink: 0 }} />
      <Typography component="span" sx={{ fontSize: 12.5, color: s.muted }}>{label}</Typography>
    </Box>
  );
}

export function MembersByPackage({ section, failed }) {
  const s = useSurface();
  const [mode, setMode] = useState("active");
  const state = sectionState(section, failed);
  const allStatuses = mode === "all";
  const rows = useMemo(() => (section?.items || [])
    .map((i) => ({
      ...i,
      value: allStatuses ? i.total : i.active,
      groupCount: allStatuses ? i.groups : i.active_groups,
      ...Object.fromEntries(PACKAGE_STATUSES.map((key) => [key, i.statuses?.[key] || 0])),
    }))
    .filter((i) => i.value > 0)
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label)), [section, allStatuses]);
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  const categories = Object.keys(PACKAGE_CATEGORY_META).filter((key) => rows.some((r) => r.category === key));
  const statuses = PACKAGE_STATUSES.filter((key) => rows.some((r) => r[key] > 0));
  const lastStatus = PACKAGE_STATUSES[PACKAGE_STATUSES.length - 1];
  const lineCount = Math.max(1, ...rows.map((r) => wrapLabel(r.label).length));
  const tickWidth = 112;

  const tick = ({ x, y, payload }) => {
    const lines = wrapLabel(payload.value);
    return (
      <text x={x} y={y} textAnchor="end" fill={s.axis} fontSize={12}>
        {lines.map((line, i) => (
          <tspan key={`${i}-${line}`} x={x - 4} dy={i === 0 ? `${0.35 - (lines.length - 1) * 0.55}em` : "1.1em"}>{line}</tspan>
        ))}
      </text>
    );
  };
  const endLabel = ({ x, y, width, height, index }) => {
    const row = rows[index];
    if (!row) return null;
    return (
      <text x={x + width + 6} y={y + height / 2} dy="0.35em" fill={s.text} fontSize={12.5} fontWeight={700}>
        {row.value.toLocaleString()}
      </text>
    );
  };

  return (
    <Panel
      icon={<IconBubble Icon={CardMembershipOutlinedIcon} color={PALETTE.blue} size={36} />}
      title="Members by package"
      subtitle="Current members per package"
      info="Today's status of every member (not affected by the date range). Couple and family members are counted one by one."
      action={(
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(e, value) => value && setMode(value)}
          aria-label="Statuses shown"
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 13, px: 1.5, py: 0.4 },
            "& .Mui-selected": { bgcolor: `${PALETTE.blue}33 !important`, color: `${s.text} !important`, fontWeight: 700 } }}>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="all">All statuses</ToggleButton>
        </ToggleButtonGroup>
      )}
    >
      <PanelState state={state} reason={section?.reason} empty={!rows.length} height={260}
        emptyText={allStatuses ? "No members yet." : "No active members."}>
        <Box sx={{ height: rows.length * Math.max(PACKAGE_ROW_HEIGHT, lineCount * 16 + 12) + 36, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 44, left: 4, bottom: 4 }} barCategoryGap="22%">
              <CartesianGrid horizontal={false} stroke={s.grid} />
              <XAxis type="number" allowDecimals={false} {...axisProps(s)} />
              <YAxis type="category" dataKey="label" width={tickWidth} interval={0} tick={tick} tickLine={false}
                axisLine={{ stroke: s.grid }} />
              <ChartTooltip cursor={{ fill: s.hover }} content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0].payload;
                const pct = total ? ((row.value / total) * 100).toFixed(1) : "0";
                return (
                  <TooltipBox title={row.label} rows={[
                    { label: allStatuses ? "Members" : "Active members", value: row.value.toLocaleString(),
                      color: PACKAGE_CATEGORY_META[row.category]?.color },
                    { label: "Share of total", value: `${pct}%` },
                    ...(row.groupCount !== null && row.groupCount !== undefined
                      ? [{ label: row.category === "couple" ? "Couples" : "Families", value: plural(row.groupCount, "group") }]
                      : []),
                    ...(allStatuses ? PACKAGE_STATUSES.filter((key) => row[key] > 0).map((key) => ({
                      label: PACKAGE_STATUS_META[key].label, value: row[key].toLocaleString(), color: PACKAGE_STATUS_META[key].color,
                    })) : []),
                  ]} />
                );
              }} />
              {allStatuses
                ? PACKAGE_STATUSES.map((key) => (
                  <Bar key={key} dataKey={key} stackId="status" name={PACKAGE_STATUS_META[key].label}
                    fill={PACKAGE_STATUS_META[key].color} maxBarSize={22} isAnimationActive={false}>
                    {key === lastStatus && <LabelList content={endLabel} />}
                  </Bar>
                ))
                : (
                  <Bar dataKey="value" name="Active members" maxBarSize={22} isAnimationActive={false}>
                    {rows.map((row) => <Cell key={row.key} fill={PACKAGE_CATEGORY_META[row.category]?.color || PALETTE.grey} />)}
                    <LabelList content={endLabel} />
                  </Bar>
                )}
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 2, rowGap: 0.75, mt: 1 }}>
          {allStatuses
            ? statuses.map((key) => <LegendDot key={key} color={PACKAGE_STATUS_META[key].color} label={PACKAGE_STATUS_META[key].label} />)
            : categories.map((key) => <LegendDot key={key} color={PACKAGE_CATEGORY_META[key].color} label={PACKAGE_CATEGORY_META[key].label} />)}
          <Typography sx={{ fontSize: 13, color: s.muted, ml: { sm: "auto" } }}>
            Total: <Box component="span" sx={{ fontWeight: 800, color: s.text }}>
              {plural(total, allStatuses ? "member" : "active member")}</Box>
          </Typography>
        </Box>
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- new vs renewals
export function NewVsRenewals({ section, failed }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const months = section?.months || [];
  const empty = months.every((m) => !m.new_registrations && !m.renewals);
  return (
    <Panel
      icon={<IconBubble Icon={AutorenewOutlinedIcon} color={PALETTE.green} size={36} />}
      title="New members & renewals"
      subtitle={`Last ${months.length || 6} months`}
    >
      <PanelState state={state} reason={section?.reason} empty={empty} height={260} emptyText="No registrations or renewals in these months.">
        <Box sx={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={months} margin={{ top: 8, right: 8, left: 8, bottom: 12 }} barGap={2}>
              <CartesianGrid vertical={false} stroke={s.grid} />
              <XAxis dataKey="month" tickFormatter={monthLabel} {...axisProps(s)} label={axisLabel(s, "Month")} />
              <YAxis allowDecimals={false} width={44} {...axisProps(s)} label={axisLabel(s, "Count", -90)} />
              <ChartTooltip cursor={{ fill: s.hover }} content={({ active, payload, label }) => (active && payload?.length ? (
                <TooltipBox title={longMonth(label)} rows={[
                  { label: "New registrations", value: payload[0].payload.new_registrations, color: PALETTE.green },
                  { label: "Renewals", value: payload[0].payload.renewals, color: PALETTE.purple },
                ]} />
              ) : null)} />
              <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
              <Bar dataKey="new_registrations" name="New registrations" fill={PALETTE.green} maxBarSize={26} isAnimationActive={false} />
              <Bar dataKey="renewals" name="Renewals" fill={PALETTE.purple} maxBarSize={26} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        {section?.renewals_available === false && (
          <Typography sx={{ fontSize: 12, color: s.muted }}>Renewals not available.</Typography>
        )}
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- inactive members
export const INACTIVE_META = {
  "14_29": { label: "14–29 days", color: PALETTE.amber },
  "30_59": { label: "30–59 days", color: "#ff9f43" },
  "60_plus": { label: "60+ days", color: PALETTE.red },
};

export function InactiveMembers({ section, failed, onBucket }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const buckets = section?.buckets || [];
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <Panel
      icon={<IconBubble Icon={PersonOffOutlinedIcon} color={PALETTE.amber} size={36} />}
      title="Inactive members"
      subtitle="No visit in 14+ days"
      info="Members who never visited count from their activation date."
    >
      <PanelState state={state} reason={section?.reason} empty={!buckets.some((b) => b.count)} height={180}
        emptyText="No inactive members.">
        {buckets.map((bucket) => {
          const meta = INACTIVE_META[bucket.key];
          return (
            <Box key={bucket.key} role="button" tabIndex={0} onClick={() => onBucket(bucket.key)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onBucket(bucket.key); }}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1, borderRadius: 1, cursor: "pointer",
                "&:hover, &:focus": { bgcolor: s.hover, outline: "none" } }}>
              <WarningAmberOutlinedIcon sx={{ color: meta.color, fontSize: 20 }} aria-hidden />
              <Typography sx={{ fontSize: 14, color: s.text, width: 92, flexShrink: 0 }}>{meta.label}</Typography>
              <Box sx={{ flex: 1, height: 20, bgcolor: s.hover, borderRadius: 1, overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${bucket.count ? Math.max(3, (bucket.count / max) * 100) : 0}%`, bgcolor: meta.color }} />
              </Box>
              <Chip size="small" label={plural(bucket.count, "member")} sx={{ fontWeight: 700, fontSize: 13 }} />
              <ChevronRightIcon sx={{ color: s.muted }} aria-hidden />
            </Box>
          );
        })}
      </PanelState>
    </Panel>
  );
}
